import React, { useState, useEffect, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resolveValidationMessage } from '../../utils/validationMessages';
import { z } from 'zod';
// import { type UserWithTenantRoles } from '../../api/services/UserControllerService';
import { UserControllerService } from '../../api/services/UserControllerService';
// import { type RoleDto, type CreateUserRequest } from '../../api/services/UserControllerService';
import { useStore } from '../../../store';
import type { CrudFormProps } from '../../components/CrudPage';
import { InfoIcon } from '../../components/InfoIcon';
import { useErrorHandler } from '../../utils/errorHandler';
import { useEntityPermissions } from '../../utils/permissions';
import { getSelectedTenant } from '../../config/environment';

// Enhanced User type to include tenant-specific role information with required id
type UserWithTenantRolesAndId = any & { 
  id: string; // Make id required
};

type UserFormData = {
  username: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  roles: string[];
  isActive: boolean;
  isTechnical: boolean;
  forcePasswordChange?: boolean;
};

// Roles allowed for technical users. Mirrors SystemRoles.allowedForTechnicalUser
// on the backend; the backend rejects anything else when isTechnical=true.
const TECHNICAL_USER_ROLES = new Set(['MOBILE_SHOPFLOOR']);

// Helper function to create validation schema with translations.
// rolesRequired is false when a SuperAdmin is edited: their rights come from
// the super_admin flag, roles have no effect for them
// (RolePermissionService short-circuits every permission check).
const createUserFormSchema = (t: (key: string) => string, rolesRequired = true) => z.object({
  username: z.string()
    .min(3, t('userManagement.form.validation.usernameMinLength'))
    .max(50, t('userManagement.form.validation.usernameMaxLength'))
    // A dot is allowed: accounts follow the pattern "firstname.lastname"
    // (e.g. f.seeanner).
    .regex(/^[a-zA-Z0-9._-]+$/, t('userManagement.form.validation.usernameInvalid')),
  email: z.string()
    .email(t('userManagement.form.validation.emailInvalid'))
    .optional()
    .or(z.literal('')), // Allow empty string
  password: z.string().optional(), // Make password optional for all cases
  passwordConfirm: z.string().optional(), // Password confirmation
  roles: rolesRequired
    ? z.array(z.string()).min(1, t('userManagement.form.validation.rolesRequired'))
    : z.array(z.string()),
  isActive: z.boolean(),
  isTechnical: z.boolean(),
  forcePasswordChange: z.boolean().optional()
}).refine((data) => {
  // If password is provided, passwordConfirm must match
  if (data.password && data.passwordConfirm) {
    return data.password === data.passwordConfirm;
  }
  return true;
}, {
  message: t('userManagement.form.validation.passwordsDoNotMatch'),
  path: ['passwordConfirm']
});

const UserManagementFormDialog: React.FC<CrudFormProps<UserWithTenantRolesAndId>> = ({ value, onClose, onSave, entityName = 'user' }) => {
  const { t } = useTranslation(['common']);
  // Flag of the EDITED user (not the logged-in one). The backend returns it
  // in the UserDto but does not accept it on save on purpose - it can only be
  // set in the database.
  const editedUserIsSuperAdmin = Boolean(value?.superAdmin);
  const userFormSchema = createUserFormSchema(t, !editedUserIsSuperAdmin);
  const userProfile = useStore((state) => state.userProfile);
  const [visible, setVisible] = useState(false);
  const [roleOptions, setRoleOptions] = useState<Array<{ text: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  const { handleError } = useErrorHandler();

  // Get permissions for user entity
  const entityPermissions = useEntityPermissions(entityName);
  
  const isSuperAdmin = userProfile?.role === 'superadmin';
  const isEditing = !!value?.id;
  
  // Determine if save button should be shown
  const canSave = isEditing ? entityPermissions.canEdit : entityPermissions.canCreate;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
    setValue
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      username: '',
      email: '',
      password: '',
      passwordConfirm: '',
      roles: [],
      isActive: true,
      isTechnical: false,
      forcePasswordChange: false
    }
  });

  // useWatch subscribes to a single field and re-renders the dialog body
  // reliably when react-hook-form's state changes — plain watch() proved to
  // miss updates inside the Syncfusion DialogComponent wrapper.
  const watchedPassword = useWatch({ control, name: 'password' });
  const watchedIsTechnical = useWatch({ control, name: 'isTechnical' });
  const watchedIsActive = useWatch({ control, name: 'isActive' });
  const watchedForcePasswordChange = useWatch({ control, name: 'forcePasswordChange' });
  const watchedRoles = useWatch({ control, name: 'roles' });
  // passwordConfirm is validated in onSubmit via data.passwordConfirm

  // When the user flips on 'technical user', force the role to the only allowed
  // option so the admin doesn't have to pick it manually and can't accidentally
  // submit a non-MOBILE_SHOPFLOOR role that the backend would reject.
  useEffect(() => {
    if (watchedIsTechnical) {
      setValue('roles', ['MOBILE_SHOPFLOOR'], { shouldValidate: false });
    }
  }, [watchedIsTechnical, setValue]);


  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const roles = await UserControllerService.getAvailableRoles();
        const options = roles
          .filter(role => role?.name && (isSuperAdmin || !role.name.toUpperCase().includes('SUPERADMIN')))
          .map(role => ({
            text: role.name!,
            value: role.name!
          }));
        setRoleOptions(options);
      } catch (error) {
        handleError(error, 'fetching roles');
        // Fallback to default roles
        setRoleOptions([
          { text: 'Admin', value: 'ADMIN' },
          { text: 'User', value: 'USER' },
          { text: 'Planner', value: 'PLANNER' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError may not be stable; runs only when visible/isSuperAdmin changes
  }, [visible, isSuperAdmin]);

  useEffect(() => {
    if (value) {
      // Extract role names from tenantRoles for the current tenant
      const storeState = useStore.getState();
      const currentTenantId = storeState.userProfile?.tenant;
      const currentTenantRoles = value.tenantRoles?.filter((tr: any) => tr.tenantId === currentTenantId) || [];
      const roleNames = currentTenantRoles.map((tr: any) => tr.roleName).filter(Boolean);
      
      reset({
        username: value.username || '',
        email: value.email || '',
        password: '', // Don't populate password for existing users
        passwordConfirm: '',
        roles: roleNames.length > 0 ? roleNames : (value.roles || []),
        isActive: value.isActive ?? true,
        isTechnical: Boolean(value.isTechnical),
        forcePasswordChange: (value as any).forcePasswordChange ?? false
      });
    } else {
      reset({
        username: '',
        email: '',
        password: '',
        passwordConfirm: '',
        roles: [],
        isActive: true,
        isTechnical: false,
        forcePasswordChange: false
      });
    }
    setVisible(true);
  }, [value, reset]);

  const onSubmit = useCallback(async (data: UserFormData) => {
    // Technical users never log in with a password (the backend blocks
    // username/password login for them and the saveData flow generates a
    // random password server-side), so password validation is skipped.
    if (!isEditing && !data.isTechnical) {
      if (!data.password || data.password.length < 8) {
        setError('password', {
          type: 'manual',
          message: t('userManagement.form.validation.passwordRequired')
        });
        return;
      }

      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
        setError('password', {
          type: 'manual',
          message: t('userManagement.form.validation.passwordComplexity')
        });
        return;
      }

      if (data.password !== data.passwordConfirm) {
        setError('passwordConfirm', {
          type: 'manual',
          message: t('userManagement.form.validation.passwordsDoNotMatch')
        });
        return;
      }
    }
    
    // Get current tenant info
    const storeState = useStore.getState();
    const currentTenantId = storeState.userProfile?.tenant;
    const selectedTenant = getSelectedTenant();
    const currentTenantName = selectedTenant?.name || '';
    
    // Convert roles array to tenantRoles format
    // For existing users, preserve roles from other tenants and update current tenant roles
    let tenantRoles: Array<{ tenantId: string; tenantName: string; roleName: string }> = [];
    if (isEditing && value?.tenantRoles) {
      // Preserve roles from other tenants
      tenantRoles = value.tenantRoles.filter((tr: any) => tr.tenantId !== currentTenantId);
    }
    
    // Add/update roles for current tenant
    if (data.roles && data.roles.length > 0 && currentTenantId) {
      data.roles.forEach(roleName => {
        tenantRoles.push({
          tenantId: currentTenantId,
          tenantName: currentTenantName,
          roleName: roleName
        });
      });
    }
    
    // Convert to UserWithTenantRolesAndId format for CrudPage
    const userWithTenantRoles: UserWithTenantRolesAndId = {
      ...data,
      id: value?.id || '', // Use existing UUID or empty string for new users
      tenantRoles: tenantRoles,
      isTechnical: Boolean(data.isTechnical),
      forcePasswordChange: data.forcePasswordChange || false,
      // Preserve admin read-only fields if editing
      ...(isEditing && value ? {
        failedLoginAttempts: (value as any).failedLoginAttempts,
        lastFailedLogin: (value as any).lastFailedLogin,
        accountLockedUntil: (value as any).accountLockedUntil
      } : {})
    };
    
    await onSave(userWithTenantRoles);
    setVisible(false);
  }, [isEditing, value, onSave, setError, t]);

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  const handleCancel = () => {
    setVisible(false);
    onClose();
  };

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  }, [isSubmitting, handleSubmit, onSubmit]);

  const handleSaveClick = () => {
    handleSubmit(onSubmit)();
  };

  const dialogContent = useCallback(() => {
    return (
      <div className="p-4 space-y-4" onKeyPress={handleKeyPress} tabIndex={0}>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('userManagement.form.username')} <span className="text-red-500">*</span>
          </label>
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                value={field.value}
                onChange={(e: any) => field.onChange(e.value)}
                placeholder={t('userManagement.form.usernamePlaceholder')}
                floatLabelType="Never"
                cssClass={errors.username ? 'e-error' : ''}
              />
            )}
          />
          {errors.username && (
            <p className="error-message">{resolveValidationMessage(t, errors.username)}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {t('userManagement.form.email')}
          </label>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                value={field.value}
                onChange={(e: any) => field.onChange(e.value)}
                placeholder={t('userManagement.form.emailPlaceholder')}
                floatLabelType="Never"
                cssClass={errors.email ? 'e-error' : ''}
              />
            )}
          />
          {errors.email && (
            <p className="error-message">{resolveValidationMessage(t, errors.email)}</p>
          )}
        </div>

        {isSuperAdmin && (
          <div className="flex items-center">
            <CheckBoxComponent
              checked={!!watchedIsTechnical}
              change={(e: any) => setValue('isTechnical', !!e.checked, { shouldDirty: true })}
            />
            <label
              className="ml-2 text-sm font-medium flex items-center cursor-pointer"
              onClick={() => setValue('isTechnical', !watchedIsTechnical, { shouldDirty: true })}
            >
              {t('userManagement.form.isTechnical')}
              <InfoIcon title={t('userManagement.form.isTechnicalHint')} />
            </label>
          </div>
        )}

        {!isEditing && !watchedIsTechnical && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('userManagement.form.password')} <span className="text-red-500">*</span>
              </label>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextBoxComponent
                    key={`password-${isEditing ? 'edit' : 'new'}`}
                    type="password"
                    value={field.value || ''}
                    onChange={(e: any) => field.onChange(e.value)}
                    placeholder={t('userManagement.form.passwordPlaceholder')}
                    floatLabelType="Never"
                    cssClass={errors.password ? 'e-error' : ''}
                    htmlAttributes={{ autocomplete: 'new-password', 'data-form-type': 'other' }}
                  />
                )}
              />
              {/* Password requirements hint */}
              <div className="mt-2 text-xs text-gray-600">
                <p className="font-medium mb-1">{t('userManagement.form.passwordRequirements.title')}</p>
                <ul className="space-y-1">
                  <li className={`flex items-center ${watchedPassword && watchedPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{watchedPassword && watchedPassword.length >= 8 ? '✓' : '○'}</span>
                    {t('userManagement.form.passwordRequirements.minLength')}
                  </li>
                  <li className={`flex items-center ${watchedPassword && /[A-Z]/.test(watchedPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{watchedPassword && /[A-Z]/.test(watchedPassword) ? '✓' : '○'}</span>
                    {t('userManagement.form.passwordRequirements.uppercase')}
                  </li>
                  <li className={`flex items-center ${watchedPassword && /[a-z]/.test(watchedPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{watchedPassword && /[a-z]/.test(watchedPassword) ? '✓' : '○'}</span>
                    {t('userManagement.form.passwordRequirements.lowercase')}
                  </li>
                  <li className={`flex items-center ${watchedPassword && /\d/.test(watchedPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{watchedPassword && /\d/.test(watchedPassword) ? '✓' : '○'}</span>
                    {t('userManagement.form.passwordRequirements.number')}
                  </li>
                </ul>
              </div>
          {errors.password && (
            <p className="error-message">{resolveValidationMessage(t, errors.password)}</p>
          )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('userManagement.form.passwordConfirm')} <span className="text-red-500">*</span>
              </label>
              <Controller
                name="passwordConfirm"
                control={control}
                render={({ field }) => (
                  <TextBoxComponent
                    type="password"
                    value={field.value || ''}
                    onChange={(e: any) => field.onChange(e.value)}
                    placeholder={t('userManagement.form.passwordConfirmPlaceholder')}
                    floatLabelType="Never"
                    cssClass={errors.passwordConfirm ? 'e-error' : ''}
                    htmlAttributes={{ autocomplete: 'new-password', 'data-form-type': 'other' }}
                  />
                )}
              />
          {errors.passwordConfirm && (
            <p className="error-message">{resolveValidationMessage(t, errors.passwordConfirm)}</p>
          )}
            </div>
          </>
        )}

        {editedUserIsSuperAdmin && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t('userManagement.form.superAdminHint')}
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-1 flex items-center">
            {t('userManagement.form.roles')}
            {!editedUserIsSuperAdmin && <span className="text-red-500 ml-1">*</span>}
            {watchedIsTechnical && <InfoIcon title={t('userManagement.form.rolesTechnicalHint')} />}
          </label>
          <MultiSelectComponent
            dataSource={watchedIsTechnical
              ? roleOptions.filter((r) => TECHNICAL_USER_ROLES.has(r.value))
              : roleOptions}
            value={watchedRoles || []}
            change={(e: any) => setValue('roles', e.value || [], { shouldDirty: true, shouldValidate: true })}
            placeholder={loading ? t('userManagement.form.rolesLoading') : t('userManagement.form.rolesPlaceholder')}
            floatLabelType="Never"
            fields={{ text: 'text', value: 'value' }}
            enabled={!loading && !watchedIsTechnical}
            cssClass={errors.roles ? 'e-error' : ''}
          />
          {errors.roles && (
            <p className="error-message">{errors.roles.message}</p>
          )}
        </div>

        <div className="flex items-center">
          <CheckBoxComponent
            checked={!!watchedIsActive}
            change={(e: any) => setValue('isActive', !!e.checked, { shouldDirty: true })}
          />
          <label
            className="ml-2 text-sm font-medium cursor-pointer"
            onClick={() => setValue('isActive', !watchedIsActive, { shouldDirty: true })}
          >
            {t('userManagement.form.isActive')}
          </label>
        </div>

        {isEditing && !watchedIsTechnical && (
          <div className="flex items-center">
            <CheckBoxComponent
              checked={!!watchedForcePasswordChange}
              change={(e: any) => setValue('forcePasswordChange', !!e.checked, { shouldDirty: true })}
            />
            <label
              className="ml-2 text-sm font-medium cursor-pointer"
              onClick={() => setValue('forcePasswordChange', !watchedForcePasswordChange, { shouldDirty: true })}
            >
              {t('userManagement.form.forcePasswordChange') || 'Force Password Change'}
            </label>
          </div>
        )}

        {isEditing && !watchedIsTechnical && (value as any) && (
          <div className="border-t pt-4 mt-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              {t('userManagement.form.adminInfo') || 'Admin Information (Read-only)'}
            </p>
            {(value as any).failedLoginAttempts !== undefined && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('userManagement.form.failedLoginAttempts') || 'Failed Login Attempts'}
                </label>
                <div className="text-sm text-gray-800">
                  {(value as any).failedLoginAttempts || 0}
                </div>
              </div>
            )}
            {(value as any).lastFailedLogin && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('userManagement.form.lastFailedLogin') || 'Last Failed Login'}
                </label>
                <div className="text-sm text-gray-800">
                  {new Date((value as any).lastFailedLogin).toLocaleString()}
                </div>
              </div>
            )}
            {(value as any).accountLockedUntil && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('userManagement.form.accountLockedUntil') || 'Account Locked Until'}
                </label>
                <div className="text-sm text-gray-800">
                  {new Date((value as any).accountLockedUntil).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [control, errors, t, isEditing, watchedPassword, watchedIsTechnical, watchedIsActive, watchedForcePasswordChange, watchedRoles, roleOptions, loading, value, handleKeyPress, isSuperAdmin, editedUserIsSuperAdmin, setValue]);

  return (
    <DialogComponent
      width="600px"
      isModal={true}
      visible={visible}
      close={handleClose}
      header={value ? t('actions.edit') : t('actions.add')}
      footerTemplate={() => (
        <div className="flex justify-end space-x-2">
          <ButtonComponent
            type="button"
            onClick={handleCancel}
            cssClass="e-outline"
          >
            {t('actions.cancel')}
          </ButtonComponent>
          {canSave && (
            <ButtonComponent
              type="button"
              onClick={handleSaveClick}
              cssClass="e-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('actions.saving') : t('actions.save')}
            </ButtonComponent>
          )}
        </div>
      )}
    >
      {dialogContent()}
    </DialogComponent>
  );
};

export default UserManagementFormDialog;
